import { useRef, useCallback, useMemo } from 'react';
import type { BoardPersona } from '@/hooks/use-permissions';
import type { BoardTerminology } from '@/hooks/use-staffing-board';
import { DEFAULT_TERMINOLOGY } from '@/hooks/use-staffing-board';

// ── Per-persona board configuration ─────────────────────────────────────────

export interface PersonaDefaults {
  /** Default project-status filter value (empty string = all). */
  projectStatusFilter: string;
  /** Whether the filter panel should be expanded on first load. */
  showFilters: boolean;
  /** Which "at-a-glance" chip to auto-activate (null = none). */
  glanceFilter: 'unstaffed' | 'missing-jsa' | 'expired-certs' | 'unassigned' | 'payroll-alerts' | null;
  /** Whether to auto-highlight expired certs on tech cards. */
  highlightExpiredCerts: boolean;
  /** Empty-state copy shown when the project list is genuinely empty (no data). */
  emptyStateHeadline: string;
  emptyStateDescription: string;
  /** Empty-state copy shown when filters/glance-chip hid everything. */
  filteredEmptyHeadline: string;
  filteredEmptyDescription: string;
  /** Subtitle copy shown under the H3ightboard logo. */
  boardSubtitle: string;
  /** Whether this persona is read-only (no drag/assign/dispatch). */
  isReadOnly: boolean;
  /** Explanation shown when a read-only user attempts a mutation. */
  readOnlyReason: string;
  /** Explanation shown when a limited-access user (e.g. Supervisor) can view but not edit. */
  limitedAccessReason?: string;
  /** Whether the compliance evidence panel should be open by default. */
  evidencePanelOpen: boolean;
}

function buildPersonaDefaults(t: BoardTerminology): Record<BoardPersona, PersonaDefaults> {
  const pl = t.project_label_plural.toLowerCase();
  return {
    owner: {
      projectStatusFilter: '',
      showFilters: false,
      glanceFilter: null,
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} in your organization`,
      emptyStateDescription:
        `Organization-wide ${pl} and their status will appear here.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting or clearing your filters to see all items.',
      boardSubtitle: 'Owner rollup — organization-wide status across all modules',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: false,
    },
    supervisor: {
      projectStatusFilter: 'active',
      showFilters: true,
      glanceFilter: null,
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} to supervise`,
      emptyStateDescription:
        `${t.project_label_plural} you supervise will appear here once added.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Awarded projects are hidden by the status filter. Open the filter panel to adjust.',
      boardSubtitle: 'Supervisor view — your active projects and crew',
      isReadOnly: false,
      readOnlyReason: '',
      limitedAccessReason:
        'Supervisor view — you can review assignments and run readiness checks, but you cannot add or move technicians on the board.',
      evidencePanelOpen: false,
    },
    payroll: {
      projectStatusFilter: '',
      showFilters: true,
      glanceFilter: 'payroll-alerts',
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} with timesheet data`,
      emptyStateDescription:
        `${t.project_label_plural} with cost and timesheet entries will appear here.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting the payroll alert filter or clearing filters to see all items.',
      boardSubtitle: 'Payroll view — cost tracking, timesheets, and labor allocation',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: false,
    },
    pm: {
      projectStatusFilter: 'active',
      showFilters: true,
      glanceFilter: 'unstaffed',
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} assigned to you`,
      emptyStateDescription:
        `${t.project_label_plural} you manage will appear here. Check daily reports and staffing levels.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting the status or unstaffed filter to see all items.',
      boardSubtitle: 'Project manager view — daily reports, staffing, and project status',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: false,
    },
    gear: {
      projectStatusFilter: '',
      showFilters: true,
      glanceFilter: null,
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} with active gear`,
      emptyStateDescription:
        `${t.project_label_plural} with deployed equipment will appear here. Check gear requests and inspection schedules.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting or clearing your filters to see all items.',
      boardSubtitle: 'Gear overview — equipment deployments, inspections, and requests',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: false,
    },
    compliance: {
      projectStatusFilter: '',
      showFilters: true,
      glanceFilter: 'missing-jsa',
      highlightExpiredCerts: true,
      emptyStateHeadline: 'All clear — no compliance gaps',
      emptyStateDescription:
        `${t.project_label_plural} missing JSAs or with expired certifications will surface here.`,
      filteredEmptyHeadline: 'No compliance issues match the current filters',
      filteredEmptyDescription: 'Try adjusting or clearing your filters to see all items.',
      boardSubtitle: 'Compliance view — JSA status, certifications, and safety signals',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: true,
    },
    crew: {
      projectStatusFilter: '',
      showFilters: true,
      glanceFilter: 'unstaffed',
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} to staff`,
      emptyStateDescription:
        `Active ${pl} will appear here. Drag technicians to assign them.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting or clearing the unstaffed filter to see all items.',
      boardSubtitle: 'Crew scheduling — assign technicians and manage staffing levels',
      isReadOnly: false,
      readOnlyReason: '',
      evidencePanelOpen: false,
    },
    crew_tech: {
      projectStatusFilter: '',
      showFilters: false,
      glanceFilter: 'unassigned',
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} on the board yet`,
      emptyStateDescription:
        'Your personal briefing is available on My Dashboard.',
      filteredEmptyHeadline: `No ${pl} match the current view`,
      filteredEmptyDescription: 'Your assignments will appear here when your supervisor adds them.',
      boardSubtitle: 'Your assignments — personal schedule and project details',
      isReadOnly: true,
      readOnlyReason:
        'Technician view — this board shows your personal schedule. Only your supervisor can add or move assignments.',
      evidencePanelOpen: false,
    },
    viewer: {
      projectStatusFilter: '',
      showFilters: false,
      glanceFilter: null,
      highlightExpiredCerts: false,
      emptyStateHeadline: `No ${pl} to display`,
      emptyStateDescription:
        `You have read-only access. ${t.project_label_plural} will appear here once they are added to the organization.`,
      filteredEmptyHeadline: `No ${pl} match the current filters`,
      filteredEmptyDescription: 'Try adjusting or clearing your filters to see all items.',
      boardSubtitle: 'Read-only view — you can browse but not modify assignments',
      isReadOnly: true,
      readOnlyReason:
        'Read-only org-wide view — you can browse all assignments, but you cannot add or move technicians on the board.',
      evidencePanelOpen: false,
    },
  };
}

export function getPersonaDefaults(persona: BoardPersona, terminology?: BoardTerminology): PersonaDefaults {
  return buildPersonaDefaults(terminology ?? DEFAULT_TERMINOLOGY)[persona];
}

// ── Hook to apply defaults once, then preserve manual overrides ─────────────

export interface PersonaDefaultsActions {
  defaults: PersonaDefaults;
  /** True after the user has manually changed any filter. */
  hasManualOverride: boolean;
  /** Call when the user manually changes a filter to flag the override. */
  markManualOverride: () => void;
}

export function useBoardPersonaDefaults(persona: BoardPersona, terminology?: BoardTerminology): PersonaDefaultsActions {
  const t = terminology ?? DEFAULT_TERMINOLOGY;
  const defaults = useMemo(() => buildPersonaDefaults(t)[persona], [t, persona]);
  const manualOverrideRef = useRef(false);

  const markManualOverride = useCallback(() => {
    manualOverrideRef.current = true;
  }, []);

  return {
    defaults,
    hasManualOverride: manualOverrideRef.current,
    markManualOverride,
  };
}
